import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import GenerateDocCertificateFields from "./GenerateDocCertificateFields";
import { GENERATE_DOC_PDF } from "./graphql";

const DEFAULT_DOC_REPORT_ROW = {
  refNo: "",
  notifiedBody: "",
  documentNo: "",
  title: "",
  editionDate: "",
};

const defaultText = (value, fallback = "") =>
  String(value === null || value === undefined ? fallback : value).trim();

const GenerateDocPdfView = ({ defaultValues = {}, onCompleted }) => {
  const imageUrls = Array.isArray(defaultValues.imageUrls)
    ? defaultValues.imageUrls.filter(Boolean)
    : [];
  const [form, setForm] = useState({
    docDirectives: defaultText(
      defaultValues.docDirectives,
      "2009/48/EC Toys safety",
    ),
    docName: defaultText(defaultValues.docName || defaultValues.sampleDescription),
    docModelNumber: defaultText(defaultValues.docModelNumber || defaultValues.styleNos),
    docSignDate: defaultText(defaultValues.docSignDate || defaultValues.issuedDate),
  });
  const [docReports, setDocReports] = useState(
    Array.isArray(defaultValues.docReports) && defaultValues.docReports.length > 0
      ? defaultValues.docReports.map((report) => ({
          ...DEFAULT_DOC_REPORT_ROW,
          ...report,
        }))
      : [{ ...DEFAULT_DOC_REPORT_ROW }],
  );
  const [generateDocPdf, generateDocPdfRes] = useMutation(GENERATE_DOC_PDF, {
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  function onChange(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function addDocReportHandler() {
    setDocReports((prev) => [...prev, { ...DEFAULT_DOC_REPORT_ROW }]);
  }

  function changeDocReportHandler(index, key, value) {
    setDocReports((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    );
  }

  function removeDocReportHandler(index) {
    setDocReports((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  }

  function generatePdfHandler() {
    const reports = docReports
      .map((report) => ({
        refNo: defaultText(report.refNo),
        notifiedBody: defaultText(report.notifiedBody),
        documentNo: defaultText(report.documentNo),
        title: defaultText(report.title),
        editionDate: defaultText(report.editionDate),
      }))
      .filter(
        (report) =>
          report.refNo ||
          report.notifiedBody ||
          report.documentNo ||
          report.title ||
          report.editionDate,
      );

    if (reports.length === 0) {
      Alert("error", "Please add at least one document row.");
      return;
    }

    if (
      reports.some(
        (report) =>
          !report.refNo ||
          !report.notifiedBody ||
          !report.documentNo ||
          !report.title ||
          !report.editionDate,
      )
    ) {
      Alert("error", "Please complete all fields for each document row.");
      return;
    }

    const articleName = defaultText(form.docName) || "Certificate";
    const filename = `${articleName.replace(/[^a-zA-Z0-9-_]+/g, "_") || "DOC"}-${Date.now()}.pdf`;
    const variables = {
      input: {
        articleName,
        modelNumber: defaultText(form.docModelNumber),
        imageUrls,
        reports,
        signDate: defaultText(form.docSignDate),
      },
      filename,
    };

    generateDocPdf({ variables }).then((res) => {
      const fileUrl = res?.data?.generateDocPdf?.fileUrl;
      if (!fileUrl) {
        Alert("error", "Failed to generate PDF.");
        return;
      }
      Alert("success", "PDF generated.");
      if (onCompleted) {
        onCompleted(fileUrl);
      }
    });
  }

  return (
    <div className="p-6 md:p-10 space-y-6 text-sm">
      <div className="card space-y-10">
        <GenerateDocCertificateFields
          form={form}
          onChange={onChange}
          imageUrls={imageUrls}
          docReports={docReports}
          addDocReportHandler={addDocReportHandler}
          changeDocReportHandler={changeDocReportHandler}
          removeDocReportHandler={removeDocReportHandler}
        />
      </div>

      <div className="flex justify-end">
        <Button primary loading={generateDocPdfRes.loading} onClick={generatePdfHandler}>
          Generate PDF
        </Button>
      </div>
    </div>
  );
};

export default GenerateDocPdfView;
